import React, { useCallback, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { FaCheckCircle, FaCloudUploadAlt, FaFileAlt, FaPlus, FaSpinner, FaTimesCircle, FaTrash } from "react-icons/fa";
import axios from "../../configApi/axiosConfig";
import { APIURL } from "../../configApi/apiConfig";

const GRUPOS_OPCIONES = [
  { value: 22, label: "Jefe Almacén (Gpo 22)" },
  { value: 24, label: "Jefe Almacén (Gpo 24)" },
  { value: 23, label: "Vendedor (Gpo 23)" },
  { value: 0,  label: "General (otros grupos)" },
];

const ESTADO_BADGE = {
  SUBIDO:      "bg-green-100  text-green-700",
  REEMPLAZADO: "bg-purple-100 text-purple-700",
};

const periodoActual = () => new Date().toISOString().slice(0, 7);

export function AdminDocumentos() {
  const [tab, setTab]               = useState("base");       // "base" | "reporte"
  const [periodo, setPeriodo]       = useState(periodoActual());
  const [docs, setDocs]             = useState([]);
  const [reporte, setReporte]       = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [showForm, setShowForm]     = useState(false);

  // Filtros del reporte
  const [filtroGrupo, setFiltroGrupo]   = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  // Formulario nuevo documento
  const [form, setForm] = useState({ nombreDocumento: "", descripcion: "", grupos: [] });
  const [archivo, setArchivo]   = useState(null);
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  // ─── Cargar base ──────────────────────────────────────────────────────────

  const cargarDocs = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axios.get(APIURL.docMen_base(periodo));
      setDocs(res.data);
    } catch { enqueueSnackbar("Error al cargar documentos base.", { variant: "error" }); }
    finally { setCargando(false); }
  }, [periodo]);

  // ─── Cargar reporte ───────────────────────────────────────────────────────

  const cargarReporte = useCallback(async () => {
    setCargando(true);
    try {
      const params = new URLSearchParams({ periodo });
      if (filtroGrupo)  params.append("idGrupo", filtroGrupo);
      if (filtroEstado) params.append("estado", filtroEstado);
      const res = await axios.get(APIURL.docMen_reporte(params.toString()));
      setReporte(res.data);
    } catch { enqueueSnackbar("Error al cargar reporte.", { variant: "error" }); }
    finally { setCargando(false); }
  }, [periodo, filtroGrupo, filtroEstado]);

  useEffect(() => {
    if (tab === "base")    cargarDocs();
    if (tab === "reporte") cargarReporte();
  }, [tab, cargarDocs, cargarReporte]);

  // ─── Crear documento base ─────────────────────────────────────────────────

  const handleCrear = async (e) => {
    e.preventDefault();
    if (!archivo) { enqueueSnackbar("Selecciona un archivo.", { variant: "warning" }); return; }
    if (!form.grupos.length) { enqueueSnackbar("Selecciona al menos un grupo.", { variant: "warning" }); return; }
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      fd.append("nombreDocumento", form.nombreDocumento);
      fd.append("descripcion",     form.descripcion);
      fd.append("periodo",         periodo);
      fd.append("grupos",          JSON.stringify(form.grupos));
      await axios.post(APIURL.docMen_baseCreate(), fd, { headers: { "Content-Type": "multipart/form-data" } });
      enqueueSnackbar("Documento creado correctamente.", { variant: "success" });
      setShowForm(false);
      setForm({ nombreDocumento: "", descripcion: "", grupos: [] });
      setArchivo(null);
      if (inputRef.current) inputRef.current.value = "";
      cargarDocs();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || "Error al crear.", { variant: "error" });
    } finally { setGuardando(false); }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Desactivar este documento?")) return;
    try {
      await axios.delete(APIURL.docMen_baseDelete(id));
      enqueueSnackbar("Documento desactivado.", { variant: "success" });
      cargarDocs();
    } catch { enqueueSnackbar("Error al eliminar.", { variant: "error" }); }
  };

  const toggleGrupo = (val) =>
    setForm((f) => ({
      ...f,
      grupos: f.grupos.includes(val) ? f.grupos.filter((g) => g !== val) : [...f.grupos, val],
    }));

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Documentos Mensuales — Admin</h1>
            <p className="text-slate-500 text-sm">Gestiona los formatos base y revisa el cumplimiento.</p>
          </div>
          <input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white shadow-sm"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {[{ key: "base", label: "Documentos Base" }, { key: "reporte", label: "Reporte de Cumplimiento" }].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ═════ TAB: Documentos Base ═════════════════════════════════════ */}
        {tab === "base" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setShowForm((v) => !v)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow transition-all"
              >
                {showForm ? <FaTimesCircle /> : <FaPlus />}
                {showForm ? "Cancelar" : "Nuevo documento"}
              </button>
            </div>

            {/* Formulario nuevo */}
            {showForm && (
              <form onSubmit={handleCrear} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
                <h3 className="font-bold text-slate-800 text-lg">Nuevo documento base — {periodo}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre del documento *</label>
                    <input
                      required
                      value={form.nombreDocumento}
                      onChange={(e) => setForm((f) => ({ ...f, nombreDocumento: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      placeholder="Ej: Reporte de Ventas Julio 2026"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
                    <input
                      value={form.descripcion}
                      onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      placeholder="Opcional"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">Grupos que deben descargar este documento *</label>
                  <div className="flex flex-wrap gap-2">
                    {GRUPOS_OPCIONES.map((g) => (
                      <button
                        key={g.value}
                        type="button"
                        onClick={() => toggleGrupo(g.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-colors ${
                          form.grupos.includes(g.value)
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-slate-200 text-slate-600 hover:border-blue-400"
                        }`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Archivo *</label>
                  <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-slate-500">
                    <FaCloudUploadAlt className="text-xl text-blue-500" />
                    {archivo ? archivo.name : "Seleccionar archivo (PDF, Word, Excel…)"}
                    <input ref={inputRef} type="file" className="hidden" onChange={(e) => setArchivo(e.target.files[0] || null)} />
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={guardando}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-bold rounded-xl shadow transition-all"
                >
                  {guardando ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                  {guardando ? "Guardando..." : "Guardar documento"}
                </button>
              </form>
            )}

            {/* Lista documentos */}
            {cargando ? (
              <div className="flex justify-center py-12 text-slate-400"><FaSpinner className="animate-spin text-3xl" /></div>
            ) : docs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FaFileAlt className="text-4xl mx-auto mb-2 text-slate-300" />
                <p>No hay documentos para el periodo <strong>{periodo}</strong>.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {docs.map((d) => (
                  <div key={d.idDocumentoBase} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
                    <FaFileAlt className="text-blue-500 text-xl flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{d.nombreDocumento}</p>
                      {d.descripcion && <p className="text-xs text-slate-500 truncate">{d.descripcion}</p>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {d.grupos?.map((g) => (
                          <span key={g.idDocumentoBaseGrupo} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                            Gpo {g.idGrupo === 0 ? "General" : g.idGrupo}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a href={d.urlArchivo} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600 underline">
                        Ver
                      </a>
                      <button onClick={() => handleEliminar(d.idDocumentoBase)} className="p-1.5 text-red-500 hover:text-red-700 transition-colors">
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═════ TAB: Reporte de cumplimiento ════════════════════════════ */}
        {tab === "reporte" && (
          <div className="space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap gap-3">
              <select
                value={filtroGrupo}
                onChange={(e) => setFiltroGrupo(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Todos los grupos</option>
                {GRUPOS_OPCIONES.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Todos los estados</option>
                <option value="SUBIDO">Subido</option>
                <option value="REEMPLAZADO">Reemplazado</option>
              </select>
            </div>

            {cargando ? (
              <div className="flex justify-center py-12 text-slate-400"><FaSpinner className="animate-spin text-3xl" /></div>
            ) : reporte.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FaFileAlt className="text-4xl mx-auto mb-2 text-slate-300" />
                <p>No hay registros para los filtros seleccionados.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Usuario", "Grupo", "Documento", "Estado", "Fecha Subida", "Actualización", "Archivo"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reporte.map((r) => (
                      <tr key={r.idDocumentoFirmado} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">{r.idUsuario}</td>
                        <td className="px-4 py-3 text-slate-600">{r.idGrupo}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate">{r.nombreDocumento}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${ESTADO_BADGE[r.estado] || "bg-slate-100 text-slate-600"}`}>
                            {r.estado}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                          {new Date(r.fechaSubida).toLocaleString("es-EC")}
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                          {r.fechaActualizacion ? new Date(r.fechaActualizacion).toLocaleString("es-EC") : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <a href={r.urlArchivoFirmado} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600 underline">
                            {r.nombreArchivoFirmado}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
